/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import { DisplayKey } from '../Game/DisplayKey';
import GameSetup from '../Game/GameSetup';
import RecallManager from '../Game/RecallManager';
import RecoveryManager from '../Game/RecoveryManager';
import LocalizationComponent from '../Localization/LocalizationComponent';
import StateComponent from '../StateManager/StateComponent';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import StateEvent from '../StateManager/StateEvent';
import { StateID } from '../StateManager/StateID';
import StateReference from '../StateManager/StateReference';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import DemoManager from '../Utility/Component/DemoManager';
import { IPopupOptions } from '../Utility/Component/Popup/IPopupModal';
import PopupModal from '../Utility/Component/Popup/PopupModal';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import Communicator from './Communicator';
import { IBonusGamePlayResult, ISlotGamePlayResult, ISpinData } from './Object/BaseGamePlayResult';
import IPlayResultComponent from './Object/IPlayResultComponent';
import IPlayResultData from './Object/IPlayResultData';
import { IPlayState } from './Object/PlayResult';
import { PlayResultData } from './PlayResultData';
import { RGSErrorCode } from './RGSErrorCode';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * An injectable cc.Component for tracking the latest play result and requesting new ones from the RGS.
 */
@cc._decorator.ccclass
export default class PlayResultComponent extends StateDependencyComponent implements IPlayResultComponent
{
    /** The state on which to request a play result from the RGS. */
    @property({ type: StateReference, visible: true })
    private _getResultOnState: StateReference = new StateReference(StateID.Request, true);

    /** Any additional states where a request should be issued. */
    @property({ type: [StateReference], visible: true })
    private _holdStates: StateReference[] = [];

    /** @hidden */
    private _communicatorRef: InjectableComponent<Communicator> = new InjectableComponent();

    /** @hidden */
    private _recoveryManagerRef: InjectableComponent<RecoveryManager> = new InjectableComponent();

    /** @hidden */
    private _gameSetupRef: InjectableComponent<GameSetup> = new InjectableComponent();

    /** @hidden */
    private _recallManagerRef: InjectableComponent<RecallManager> = new InjectableComponent();

    /** @hidden */
    private _demoManagerRef: InjectableComponent<DemoManager> = new InjectableComponent();

    /** @hidden */
    private _popupModalRef: InjectableComponent<PopupModal> = new InjectableComponent();

    /** @hidden */
    private _localRef: InjectableComponent<LocalizationComponent> = new InjectableComponent();

    /** @hidden */
    private _activeState: StateComponent = null;
    /** @hidden */
    private _resultData: IPlayResultData;
    /** @hidden */
    protected playState: IPlayState;
    public get PlayState(): IPlayState { return this.playState; }
    /** The current result data. */
    public get ResultData(): IPlayResultData { return this._resultData; }

    /** @hidden */
    protected setContexts(): void
    {
        super.setContexts();
        this._communicatorRef.SetContext(Communicator);
        this._recoveryManagerRef.SetContext(RecoveryManager);
        this._gameSetupRef.SetContext(GameSetup);
        this._recallManagerRef.SetContext(RecallManager);
        this._demoManagerRef.SetContext(DemoManager);
        this._popupModalRef.SetContext(PopupModal);
        this._localRef.SetContext(LocalizationComponent);
    }

    /** @hidden */
    protected start(): void
    {
        super.start(this._holdStates, [this._getResultOnState]);
    }

    /** @hidden */
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        const recoveryManager: RecoveryManager = this._recoveryManagerRef.Value;
        const recallManager: RecallManager = this._recallManagerRef.Value;

        this._activeState = event.State;

        let funcRequest: Function = null;

        if (recoveryManager && recoveryManager.Recovering)
        {
            funcRequest = () =>
            {
                const result: string
                    = this._gameSetupRef.Value.GetPreviousResult(this._gameSetupRef.Value.CurrentRound,
                        recoveryManager.RecoveryCurrentPlayID);
                this.onResult.call(this, result);
            };
        }
        else if (recallManager && recallManager.Recalling)
        {
            funcRequest = () =>
            {
                this._communicatorRef.Value.RequestPreviousResult(
                    recallManager.RecallRoundID,
                    recallManager.RecallPlayID,
                    this.onResult.bind(this)
                );
            };
        }
        else
        {
            recoveryManager.EndRecoveryMode();

            const demoManager: DemoManager = this._demoManagerRef.Value;
            if (demoManager && demoManager.ShouldDemoSpin)
            {
                funcRequest = () => demoManager.RequestDemoSpin(this.onResult.bind(this));
            }
            else
            {
                funcRequest = () => this._communicatorRef.Value.RequestPlayResult(this.onResult.bind(this));
            }
        }

        if (event.State === this._getResultOnState.Value)
        {
            funcRequest();
        }

        this._holdStates.forEach(stateRef =>
        {
            if (event.State === stateRef.Value)
            {
                funcRequest();
            }
        });

        if (recallManager.Recalling)
        {
            recallManager.RecallPlayID++;
        }
    }

    /** @hidden */
    private _onResultFunctions: (() => any)[] = [];
    /**
     * Register a function to be called on play result received.
     * @param OnResult The function to be called.
     */
    public OnResult(fnOnResult: () => any): void
    {
        this._onResultFunctions.push(fnOnResult);
    }

    public get OnResultHandler(): (resultJSON: string) => void { return this.onResult; }

    /** @hidden */
    private onResult = (resultJSON: string): void =>
    {
        try
        {
            this.playState = JSON.parse(resultJSON);
        }
        catch (error)
        {
            CLog.error('Eror parsing result.', this, LogTypes.Communicator);
            return;
        }

        if (this.playState.errorCode === RGSErrorCode[RGSErrorCode.REQUESTED_PLAY_ALREADY_EXIST])
        {
            let resultPSN: number = this.playState.playResult.PlaySequenceNumber;
            let lastPSN: number = this.ResultData
                ? this.ResultData.PlayResult.PlaySequenceNumber
                : this._gameSetupRef.WithValue(gameSetup => gameSetup.CurrentRound, 0);

            if (resultPSN - lastPSN > 1)
            {
                this.promptExit();
                return;
            }
        }

        // When first starting up, result data is stored in play state, if there pull it out.
        // tslint:disable-next-line:no-string-literal
        const playStateJSON: string = this.playState['playState'];
        if (playStateJSON)
        {
            // Stored as json string property
            this.playState = JSON.parse(playStateJSON);
        }

        if (!this.playState || !this.playState.playResult)
        {
            CLog.error("Malformed requestPlayResult response.", this, LogTypes.Communicator);
        }
        else
        {
            this._resultData = new PlayResultData(this.playState.playResult);
        }

        this._onResultFunctions.forEach(fnOnResult => fnOnResult());
        this.completeState(this._activeState);
    }

    private promptExit(): void
    {
        const options: IPopupOptions =
        {
            MessageId: RGSErrorCode[RGSErrorCode.REQUESTED_PLAY_ALREADY_EXIST],
            Title: this._localRef.Value.GetStringResource(`Errors/Title`)
        };
        let message: string = this.playState.errorMessage;

        if (this._gameSetupRef.Value.ShouldDisplay(DisplayKey.Lobby) === false)
        {
            message += ` ${this._localRef.Value.GetStringResource("Errors/Please_return_to_lobby")}`;
            options.OkButtonText = this._localRef.Value.GetStringResource('Errors/Reload');
            options.FnOnOK = () => this._gameSetupRef.Value.ReloadPage();
        }
        else
        {
            options.OkButtonText = this._localRef.Value.GetStringResource('Errors/ExitToLobby');
            options.FnOnOK = () => this._gameSetupRef.Value.NavigateToLobby();
        }

        this._popupModalRef.WithValue(popup => popup.Show(message, options));
    }

    /**
     * @returns The result data for the normal spin.
     */
    public GetBaseGameResult(): ISlotGamePlayResult
    {
        if (!this._resultData) { return null; }
        return this._resultData.PlayResult.BaseGamePlayResult.SlotGamePlayResult;
    }

    /**
     * @returns The result data for any bonus spin.
     */
    public GetBonusGameResult(): ISlotGamePlayResult
    {
        if (!this._resultData) { return null; }
        return this._resultData.PlayResult.BonusGamePlayResult.SlotGamePlayResult;
    }

    public GetBaseSpinData(): ISpinData
    {
        return this._resultData
            ? this._resultData.GetBaseSpinData()
            : null;
    }

    public GetBonusSpinData(): ISpinData
    {
        return this._resultData
            ? this._resultData.GetBonusSpinData()
            : null;
    }

    /**
     * @returns The result data for current game type.
     */
    public GetCurrentGameResult(): ISlotGamePlayResult
    {
        return this.IsBonusSpinActive()
            ? this.GetBonusGameResult()
            : this.GetBaseGameResult();
    }

    /**
     * @returns Any bonus game flag present.
     */
    public GetBonusFlag(): number
    {
        return this._resultData ? this._resultData.PlayResult.BonusGameFlag : 0;
    }

    /**
     * @returns If any bonus spin is active.
     */
    public IsBonusSpinActive(): boolean
    {
        if (!this.HasBonus()) { return false; }

        const bonusResult: ISlotGamePlayResult = this.GetBonusGameResult();
        if (!bonusResult) { return false; }

        const bonusGameResult: IBonusGamePlayResult = this._resultData.PlayResult.BonusGamePlayResult;
        if (bonusGameResult.SpinData && bonusGameResult.SpinData.eActivationStatus !== 0) { return true; }

        return !!bonusGameResult.CurrentOutcome;
    }

    /**
     * @returns Does this game have a bonus flag.
     */
    public HasBonus(): boolean
    {
        return this.GetBonusFlag() !== 0;
    }

    /**
     * @returns The number of remaining spins in the bonus game.
     */
    public GetRemainingSpins(): number
    {
        let remainingFreeSpins: number = this._resultData.PlayResult.BonusGamePlayResult.RemainingFreeSpins;

        if (!remainingFreeSpins && remainingFreeSpins !== 0)
        {
            let curSpin: number = this._resultData.PlayResult.BonusGamePlayResult.SpinData.nPlayNumber;
            let totalSpins: number = this._resultData.PlayResult.BonusGamePlayResult.SpinData.nTotalPlays;
            return totalSpins - curSpin;
        }

        return remainingFreeSpins;
    }
}
